<template>
  <b-container
    fluid
    class="ticket-create-bg"
    aria-label="Formulaire de création de ticket de support logistique"
    role="main"
  >
    <b-row no-gutters>
      <b-col
        cols="12"
        class="px-0 d-flex justify-content-center align-items-center"
        style="min-height: 90vh;"
      >
        <b-card
          class="ticket-create-card shadow-lg border-0"
          style="max-width: 650px; width: 100%; border-radius: 1.25rem; box-shadow: 0 4px 32px 0 rgba(232,0,0,0.10); margin: 3.5rem 0 2.5rem 0;"
        >
          <template #header>
            <div class="d-flex align-items-center mb-2">
              <feather-icon
                icon="Edit3Icon"
                class="mr-2 text-danger"
                size="28"
                aria-label="Créer un ticket de suivi"
              />
              <span class="h4 font-weight-bold mb-0">Créer un ticket de suivi</span>
            </div>
            <div class="text-muted small mb-2">
              Merci de remplir le formulaire pour ouvrir une demande de support liée à vos colis.
            </div>
            <div
              class="sr-only"
              aria-live="polite"
            >
              Tous les champs marqués d'un astérisque sont obligatoires. Utilisez la touche Tab pour naviguer entre les champs du formulaire.
            </div>
          </template>
          <b-form
            novalidate
            class="ticket-create-form"
            aria-label="Formulaire de création de ticket de support logistique"
            @submit.prevent="submit"
          >
            <!-- Catégorie -->
            <b-form-group
              label="Catégorie du ticket"
              label-for="category"
              label-cols-sm="12"
              label-align-sm="left"
              label-class="font-weight-bold text-dark mb-1"
              description="Sélectionnez la catégorie la plus appropriée."
              :state="categoryState"
              class="mb-4"
            >
              <div class="input-icon-group modern-input">
                <feather-icon
                  icon="TagIcon"
                  size="20"
                  class="input-icon"
                  aria-label="Catégorie du ticket"
                />
                <v-select
                  id="category"
                  v-model="form.category"
                  :options="categoryOptions"
                  :reduce="cat => cat.value"
                  :get-option-label="option => option.label"
                  :clearable="false"
                  placeholder="Sélectionner la catégorie"
                  :class="['modern-select', { 'is-invalid': categoryState === false }]"
                  :no-options="categoryOptions.length === 0"
                  no-options-text="Aucune catégorie disponible"
                  aria-required="true"
                  @input="onCategoryChange"
                />
              </div>
              <b-form-invalid-feedback>
                La catégorie est requise.
              </b-form-invalid-feedback>
              <div
                v-if="selectedCategoryDescription"
                class="category-description mt-2"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="mr-1 text-info"
                  aria-label="Description de la catégorie"
                />
                <span>{{ selectedCategoryDescription }}</span>
              </div>
            </b-form-group>

            <!-- Titre -->
            <b-form-group
              label="Titre du ticket"
              label-for="title"
              label-cols-sm="12"
              label-align-sm="left"
              label-class="font-weight-bold text-dark mb-1"
              description="Un titre court et explicite pour votre demande de suivi (optionnel)."
              :state="titleState"
              class="mb-4"
            >
              <div class="input-icon-group modern-input">
                <feather-icon
                  icon="TypeIcon"
                  size="20"
                  class="input-icon"
                  aria-label="Titre du ticket"
                />
                <b-form-input
                  id="title"
                  v-model.trim="form.title"
                  :state="titleState"
                  required
                  placeholder="Saisir le titre du ticket de suivi"
                  maxlength="120"
                  autocomplete="off"
                  aria-label="Titre du ticket"
                />
              </div>
              <b-form-invalid-feedback v-if="false">
                <!-- No validation, so never shown -->
              </b-form-invalid-feedback>
            </b-form-group>

            <!-- Description -->
            <b-form-group
              label="Description du problème"
              label-for="description"
              label-cols-sm="12"
              label-align-sm="left"
              label-class="font-weight-bold text-dark mb-1"
              description="Décrivez précisément le problème ou la demande de suivi (optionnel)."
              :state="descState"
              class="mb-4"
            >
              <vue-editor
                id="description"
                v-model="form.description"
                :state="descState"
                placeholder="Décrire le problème ou la demande de suivi"
                :editor-toolbar="editorToolbar"
                style="min-height: 150px; background: #f8f9fa; border-radius: 0.75rem; border: 1.5px solid #e80000;"
                aria-label="Description du problème"
              />
              <b-form-invalid-feedback v-if="false">
                <!-- No validation, so never shown -->
              </b-form-invalid-feedback>
            </b-form-group>

            <!-- Colis liés -->
            <b-form-group
              label="Colis liés"
              label-for="orders"
              label-cols-sm="12"
              label-align-sm="left"
              label-class="font-weight-bold text-dark mb-1"
              description="Sélectionnez un ou plusieurs colis concernés par ce ticket de suivi."
              :state="ordersState"
              class="mb-4"
            >
              <div class="input-icon-group modern-input">
                <feather-icon
                  icon="PackageIcon"
                  size="20"
                  class="input-icon"
                  aria-label="Colis liés"
                />
                <v-select
                  id="orders"
                  v-model="form.orders"
                  :options="orderOptions"
                  :reduce="order => order.value"
                  :get-option-label="option => option.label"
                  :multiple="true"
                  :taggable="false"
                  :close-on-select="false"
                  :clearable="true"
                  placeholder="Sélectionner les colis"
                  :class="['modern-select', { 'is-invalid': ordersState === false }]"
                  :no-options="orderOptions.length === 0"
                  no-options-text="Aucun colis disponible"
                  label="label"
                  aria-label="Colis liés"
                >
                  <template #option="option">
                    <span>
                      {{ option.label }}
                    </span>
                  </template>
                  <template #selected-option="option">
                    <span>{{ option.label }}</span>
                  </template>
                </v-select>
              </div>
              <b-form-invalid-feedback>
                Veuillez sélectionner au moins un colis.
              </b-form-invalid-feedback>
            </b-form-group>

            <!-- Submit -->
            <div class="d-flex flex-column align-items-center mt-4">
              <b-button
                type="submit"
                variant="danger"
                size="lg"
                class="font-weight-bold px-5 py-2 modern-btn modern-btn-animate"
                :disabled="!formIsValid || loading"
                style="border-radius: 0.75rem; font-size: 1.15rem; box-shadow: 0 2px 8px 0 rgba(232,0,0,0.08);"
                aria-label="Créer le ticket de suivi"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-2"
                />
                Créer le ticket de suivi
              </b-button>
              <div class="text-muted mt-2 small">
                Vous recevrez une notification après la création du ticket.
              </div>
            </div>
            <div
              v-if="loading"
              class="form-loading-overlay"
            >
              <b-spinner
                variant="danger"
                label="Chargement..."
              />
            </div>
            <transition name="fade">
              <div
                v-if="showSuccess"
                class="success-feedback"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  class="mr-2 text-success"
                  size="22"
                  aria-label="Succès"
                />
                Ticket créé avec succès !
              </div>
            </transition>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from '@/libs/axios'
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BFormInvalidFeedback,
  BSpinner,
} from 'bootstrap-vue'
import { VueEditor } from 'vue2-editor'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import 'vue2-editor/dist/vue2-editor.css'

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormInvalidFeedback,
    BSpinner,
    VueEditor,
    vSelect,
  },
  data() {
    return {
      form: {
        title: '',
        description: '',
        category: null,
        orders: [],
      },
      categories: [],
      orders: [],
      loading: false,
      showSuccess: false,
      editorToolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      touched: {
        title: false,
        description: false,
        category: false,
        orders: false,
      },
      selectedCategoryDescription: '',
    }
  },
  computed: {
    categoryOptions() {
      return this.categories.map(cat => ({
        value: cat.id,
        label: cat.display_name || cat.name,
        description: cat.description,
      }))
    },
    orderOptions() {
      return this.orders.map(order => ({
        value: order.id,
        label: `#${order.id} - ${order.name || order.id}`,
      }))
    },
    titleState() {
      // Title is optional, so always return null (no validation)
      return null
    },
    descState() {
      // Description is optional, so always return null (no validation)
      return null
    },
    categoryState() {
      // Only category is required
      return this.touched.category ? !!this.form.category : null
    },
    ordersState() {
      // Orders are optional
      return null
    },
    formIsValid() {
      // Only category is required
      return !!this.form.category
    },
  },
  watch: {
    'form.title': function handleTitle() { this.touched.title = true },
    'form.description': function handleDescription() { this.touched.description = true },
    'form.category': function handleCategory() { this.touched.category = true },
    'form.orders': function handleOrders() { this.touched.orders = true },
  },
  async mounted() {
    await this.fetchCategories()
    await this.fetchOrders()
  },
  methods: {
    async fetchCategories() {
      const { data } = await axios.get('/api/support/categories/')
      this.categories = data
    },
    async fetchOrders() {
      const { data } = await axios.get('/api/orders/eligible-for-ticket/')
      this.orders = data
    },
    async submit() {
      // Mark all as touched for validation
      this.touched.category = true
      if (!this.formIsValid) return

      this.loading = true
      this.showSuccess = false
      try {
        const payload = {
          category: this.form.category,
        }
        // Only include optional fields if filled
        if (this.form.title) payload.title = this.form.title
        if (this.form.description) payload.description = this.form.description
        if (this.form.orders && this.form.orders.length > 0) payload.orders = this.form.orders

        // Await the ticket creation and get the response
        const { data } = await axios.post('/api/support/tickets/', payload)
        this.showSuccess = true
        setTimeout(() => {
          this.showSuccess = false
          // Redirect to detail page with the new ticket id
          this.$router.push({ name: 'SupportTicketDetail', params: { id: data.id } })
        }, 1200)
        this.$bvToast.toast('Ticket créé avec succès.', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
      } catch (e) {
        this.$bvToast.toast('Erreur lors de la création du ticket.', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },
    onCategoryChange(categoryId) {
      const selected = this.categories.find(cat => cat.id === categoryId)
      this.selectedCategoryDescription = selected && selected.description ? selected.description : ''
    },
  },
}
</script>

<style scoped>
.ticket-create-bg {
  background: linear-gradient(135deg, #fff 60%, #ffeaea 100%);
  min-height: 100vh;
  padding-bottom: 3rem;
  width: 100%;
  max-width: 100%;
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
  padding-right: 0;
}

.ticket-create-card {
  border-radius: 1.25rem;
  box-shadow: 0 4px 32px 0 rgba(232,0,0,0.10);
  border: none;
  padding: 2.5rem 2.5rem;
  width: 100%;
  max-width: 650px;
  margin: 3.5rem 0 2.5rem 0;
  background: #fff;
}

.ticket-create-form {
  width: 100%;
  background: transparent;
}

.b-form-group {
  margin-bottom: 2.2rem !important;
}

.input-icon-group.modern-input {
  background: #f8f9fa;
  border-radius: 0.75rem;
  border: 1.5px solid #e80000;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  margin-bottom: 0.2rem;
  min-height: 48px;
  transition: border 0.2s, box-shadow 0.2s;
}
.input-icon-group.modern-input:focus-within {
  border-color: #b30000;
  box-shadow: 0 0 0 2px #ffeaea;
}

.input-icon {
  position: absolute;
  left: 18px;
  z-index: 2;
  color: #e80000;
}

.input-icon + .form-control,
.input-icon + .v-select {
  padding-left: 2.2rem !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.modern-select .vs__dropdown-toggle {
  border: none !important;
  background: transparent !important;
  min-height: 38px;
  font-size: 1rem;
  box-shadow: none !important;
}

.modern-select .vs__selected {
  background: #ffeaea;
  border-radius: 6px;
  padding: 0.25em 0.5em;
  margin-right: 0.25em;
  color: #e80000;
  font-weight: 600;
  transition: background 0.2s;
}

.modern-select .vs__dropdown-menu {
  background: #fff !important;
  border-radius: 0.75rem !important;
  box-shadow: 0 4px 24px 0 rgba(232,0,0,0.10);
  border: 1.5px solid #e80000;
  padding: 0.25rem 0;
}

.modern-select .vs__dropdown-option {
  background: #fff !important;
  color: #1d1d1b !important;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.5rem 1.25rem;
  border-radius: 0.5rem;
  margin: 0 0.5rem 0.25rem 0.5rem;
  transition: background 0.15s, color 0.15s;
}

.modern-select .vs__dropdown-option--selected,
.modern-select .vs__dropdown-option--highlight {
  background: #ffeaea !important;
  color: #e80000 !important;
  font-weight: 700;
}

.modern-select .vs__dropdown-option[aria-disabled='true'] {
  color: #bdbdbd !important;
  background: #f5f5f5 !important;
  font-style: italic;
  cursor: not-allowed;
}

.modern-btn {
  background: linear-gradient(90deg, #e80000 0%, #ff5e62 100%);
  color: #fff !important;
  border: none;
  font-weight: 700;
  letter-spacing: 0.5px;
  transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
}
.modern-btn-animate:hover, .modern-btn-animate:focus {
  background: linear-gradient(90deg, #b30000 0%, #ff5e62 100%);
  color: #fff !important;
  box-shadow: 0 4px 16px 0 rgba(232,0,0,0.18);
  transform: scale(1.04);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.success-feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #28a745;
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 1.5rem;
  background: #e6f9ed;
  border-radius: 0.75rem;
  padding: 0.75rem 1.5rem;
  box-shadow: 0 2px 8px 0 rgba(40,167,69,0.08);
}

.form-loading-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  z-index: 10;
}

.category-description {
  background: #f8f9fa;
  border-left: 3px solid #e80000;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  color: #1d1d1b;
  font-size: 0.97rem;
  display: flex;
  align-items: center;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>
